import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HeaderContainer,
  HeaderRow,
  HeaderLeft,
  HeaderRight,
  TagsGroup,
  TagSeparator,
  AvatarProfile,
  AvatarDescription,
  Name,
  LastConnection,
  TabSwitcherContainer,
  TabSubContainer,
  HeaderMasterContainer,
  TabRowContainer,
} from "./Header.styled";
import { Avatar, Tabs, Skeleton, Typography, Button, Icon, Tag } from "@ann/web-components";
import { generateMockAvatarUrl } from "../../../../../utils/avatarUtils";
import HeaderEmailModal from "../HeaderEmailModal/HeaderEmailModal";
import HeaderPhoneModal from "../HeaderPhoneModal/HeaderPhoneModal";
import RemoveConfirmModal from "./RemoveConfirmModal";
import { useContactHeader } from "../../hooks/useContactHeader";
import { useContacts } from "../../context/ContactsContext";
import { useToast } from "../../../../../components/toast";

const TAB_TO_ROUTE_MAP = {
  overview: "overview",
  activity: "activity",
  snapshot: "snapshot",
  garage: "garage",
  appointments: "appointments",
  "repair-orders": "repairs-orders",
  tasks: "tasks",
};

const TAB_IDS = Object.keys(TAB_TO_ROUTE_MAP);

const getTabIndexFromId = (id) => {
  const idx = TAB_IDS.indexOf(id);
  return idx >= 0 ? idx : 0;
};

const getCurrentTabFromRoute = (pathname) => {
  const pathSegments = pathname.split("/");
  const ucpIndex = pathSegments.findIndex((segment) => segment === "ucp");
  if (ucpIndex === -1) return "overview";
  if (pathSegments.length <= ucpIndex + 1) return "overview";
  for (let i = ucpIndex + 1; i < pathSegments.length; i++) {
    const segment = pathSegments[i];
    for (const [tab, route] of Object.entries(TAB_TO_ROUTE_MAP)) {
      if (route === segment) return tab;
    }
  }
  return "overview";
};

const Header = observer(({ onOpenChat }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { contactDetails, loading, hasContact } = useContactHeader();
  const { deleteEmail, deletePhone, selectedContact } = useContacts();
  const contactId = selectedContact?.partyUuid;

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isRemoveEmailModalOpen, setIsRemoveEmailModalOpen] = useState(false);
  const [isRemovePhoneModalOpen, setIsRemovePhoneModalOpen] = useState(false);
  const [pendingRemoveEmail, setPendingRemoveEmail] = useState(null);
  const [pendingRemovePhone, setPendingRemovePhone] = useState(null);

  const [selectedTab, setSelectedTab] = useState(getCurrentTabFromRoute(location.pathname));

  useEffect(() => {
    setSelectedTab(getCurrentTabFromRoute(location.pathname));
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    const route = TAB_TO_ROUTE_MAP[tabId];
    const pathSegments = location.pathname.split("/");
    const ucpIndex = pathSegments.findIndex((segment) => segment === "ucp");
    const contactIdFromPath = pathSegments[ucpIndex + 1];

    navigate(`/v2/ucp/${contactIdFromPath}/${route}`);
  };

  const handleTabsChange = (index) => {
    handleTabChange(TAB_IDS[index]);
  };

  const handleOpenEmailModal = useCallback(() => setIsEmailModalOpen(true), []);
  const handleCloseEmailModal = useCallback(() => setIsEmailModalOpen(false), []);
  const handleOpenPhoneModal = useCallback(() => setIsPhoneModalOpen(true), []);
  const handleClosePhoneModal = useCallback(() => setIsPhoneModalOpen(false), []);

  const handleRequestRemoveEmail = useCallback((emailId, emailAddress) => {
    setIsEmailModalOpen(false);
    setPendingRemoveEmail({ id: emailId, email: emailAddress });
    setIsRemoveEmailModalOpen(true);
  }, []);

  const handleRequestRemovePhone = useCallback((phoneId, phoneNumber) => {
    setIsPhoneModalOpen(false);
    setPendingRemovePhone({ id: phoneId, phone: phoneNumber });
    setIsRemovePhoneModalOpen(true);
  }, []);

  const handleCloseRemoveEmailModal = useCallback(() => {
    setIsRemoveEmailModalOpen(false);
    setPendingRemoveEmail(null);
    setIsEmailModalOpen(true);
  }, []);

  const handleCloseRemovePhoneModal = useCallback(() => {
    setIsRemovePhoneModalOpen(false);
    setPendingRemovePhone(null);
    setIsPhoneModalOpen(true);
  }, []);

  const handleConfirmRemoveEmail = useCallback(async () => {
    if (!contactId || !pendingRemoveEmail) return;
    try {
      await deleteEmail(contactId, pendingRemoveEmail.id);
      toast.success({
        title: "Email removed",
        message: "The email was removed from this contact successfully.",
      });
    } catch (err) {
      console.error("Error removing email:", err);
      toast.error({
        title: "Error",
        message: err?.message || "Failed to remove email. Please try again.",
      });
      throw err;
    } finally {
      setIsRemoveEmailModalOpen(false);
      setPendingRemoveEmail(null);
    }
  }, [contactId, pendingRemoveEmail, deleteEmail, toast]);

  const handleConfirmRemovePhone = useCallback(async () => {
    if (!contactId || !pendingRemovePhone) return;
    try {
      await deletePhone(contactId, pendingRemovePhone.id);
      toast.success({
        title: "Phone number removed",
        message: "The phone number was removed from this contact successfully.",
      });
    } catch (err) {
      console.error("Error removing phone:", err);
      toast.error({
        title: "Error",
        message: err?.message || "Failed to remove phone. Please try again.",
      });
      throw err;
    } finally {
      setIsRemovePhoneModalOpen(false);
      setPendingRemovePhone(null);
    }
  }, [contactId, pendingRemovePhone, deletePhone, toast]);

  if (loading && !hasContact) {
    return (
      <HeaderMasterContainer>
        <HeaderContainer>
          <HeaderRow>
            <HeaderLeft>
              <AvatarProfile>
                <Skeleton borderRadius="50%" height="32px" width="32px" />
              </AvatarProfile>
              <AvatarDescription>
                <Skeleton height="20px" width="200px" />
                <Skeleton height="16px" width="150px" />
              </AvatarDescription>
            </HeaderLeft>
          </HeaderRow>
          {/* Temporary disabled
          <ButtonsDetails>
            <Skeleton height="40px" width="40px" />
            <ButtonsContainer>
              <Skeleton height="40px" width="120px" />
            </ButtonsContainer>
          </ButtonsDetails> */}
        </HeaderContainer>
        <TabRowContainer>
          <TabSwitcherContainer>
            <TabSubContainer>
              <Skeleton height="40px" width="600px" />
            </TabSubContainer>
          </TabSwitcherContainer>
          {/* Temporary disabled
          <DepartmentSelectWrapper>
            <Skeleton height="40px" width="150px" />
          </DepartmentSelectWrapper> */}
        </TabRowContainer>
      </HeaderMasterContainer>
    );
  }

  if (!hasContact || !contactDetails) return null;

  return (
    <HeaderMasterContainer>
      <HeaderContainer>
        <HeaderRow>
          <HeaderLeft>
            <AvatarProfile>
              <Avatar
                alt={contactDetails.name || "Default avatar"}
                size="lg"
                src={generateMockAvatarUrl(contactDetails.name) || "https://i.pravatar.cc/32"}
              />
            </AvatarProfile>
            <TagsGroup>
              <Typography category="subtitle" weight="bold" size="small" color="title">
                {contactDetails.name}
              </Typography>
              <TagSeparator>•</TagSeparator>
              <Icon name="adminPanelSettings" size="small" color="neutral" />
              <Typography category="body" weight="regular" size="small" color="body">
                {contactDetails.role}
              </Typography>
              <TagSeparator>•</TagSeparator>
              <Icon name="work" size="small" color="neutral" />
              <Typography category="body" weight="regular" size="small" color="body">
                {contactDetails.company}
              </Typography>
              <TagSeparator>•</TagSeparator>
              <Typography category="body" weight="regular" size="small" color="body">
                {contactDetails.ownerName}
              </Typography>
              <TagSeparator>•</TagSeparator>
              <Tag
                color="informative"
                onClick={() => {}}
                onClose={() => {}}
                size="medium"
                text="Appointment Today"
              />
            </TagsGroup>
          </HeaderLeft>
          <HeaderRight>
            <Button
              icon="aiChat"
              label=""
              size="small"
              type="gradient"
              onClick={() => onOpenChat?.()}
            />
            <Icon name="chevronRight" size="medium" color="neutral" />
          </HeaderRight>
        </HeaderRow>

        {/* Contact details — commented per request. To restore: add ContactDetailsWrapper, PersonalDetails, PersonalDetailsItem, CircleButton (styled), IconWithTooltip (web-components)
        <ContactDetailsWrapper>
          <PersonalDetails>
            <div>
              <IconWithTooltip name="call" tooltip="Make a call" />
              <PersonalDetailsItem>{contactDetails.phone}</PersonalDetailsItem>
              <CircleButton onClick={handleOpenPhoneModal}>
                +{contactDetails.additionalPhonesCount || ""}
              </CircleButton>
            </div>
            <div>
              <IconWithTooltip name="email" tooltip="Send Mail" />
              <PersonalDetailsItem>{contactDetails.email}</PersonalDetailsItem>
              <CircleButton onClick={handleOpenEmailModal}>
                +{contactDetails.additionalEmailsCount || ""}
              </CircleButton>
            </div>
          </PersonalDetails>
          <PersonalDetails>
            <div>
              <IconWithTooltip name="locationOn" tooltip="Location" />
              <PersonalDetailsItem>
                {contactDetails.address || 'No address available'}
              </PersonalDetailsItem>
            </div>
            {contactDetails.postalCode !== 'N/A' && (
              <div>
                <IconWithTooltip name="tag" tooltip="Postal Code" />
                <PersonalDetailsItem>{contactDetails.postalCode}</PersonalDetailsItem>
              </div>
            )}
          </PersonalDetails>
        </ContactDetailsWrapper>
        {contactDetails.company !== 'N/A' && (
          <PersonalDetails>
            <div>
              <IconWithTooltip name="work" tooltip="Work" />
              <PersonalDetailsItem>{contactDetails.company}</PersonalDetailsItem>
            </div>
          </PersonalDetails>
        )} */}
        
        {/* Temporary disabled
        <ButtonsDetails>
          <ActionsButton>
            <Icon name="edit" color="#5C5C5C" />
          </ActionsButton>
          <ButtonsContainer>
            <Button type="secondary" size="medium" label="View Family" pill />
          </ButtonsContainer>
        </ButtonsDetails> */}
      </HeaderContainer>
      <TabRowContainer>
        <TabSwitcherContainer>
          <TabSubContainer>
            <Tabs
              key={selectedTab}
              defaultIndex={getTabIndexFromId(selectedTab)}
              size="medium"
              onChange={handleTabsChange}
            >
              <Tabs.List>
                <Tabs.Tab index={0} iconLeft="person">Overview</Tabs.Tab>
                <Tabs.Tab index={1} iconLeft="person">Activity</Tabs.Tab>
                {/*<Tabs.Tab index={2} iconLeft="person">Snapshot</Tabs.Tab>
                <Tabs.Tab index={3} iconLeft="person">Garage</Tabs.Tab>
                <Tabs.Tab index={4} iconLeft="person">Appointments</Tabs.Tab>
                <Tabs.Tab index={5} iconLeft="person">Repair Orders</Tabs.Tab>
                <Tabs.Tab index={6} iconLeft="person">Tasks</Tabs.Tab>} */}
              </Tabs.List>
            </Tabs>
          </TabSubContainer>
        </TabSwitcherContainer>
        {/* Temporary disabled
        <DepartmentSelectWrapper>
          <Select
            placeholder="Sales"
            onChange={(value) => console.log("Selected:", value)}
            size="small"
            value="sales"
            minWidth="100px"
          >
            <Select.Menu>
              <Select.Item value="sales" label="Sales">
                Sales
              </Select.Item>
              <Select.Item value="support" label="Support">
                Support
              </Select.Item>
              <Select.Item value="marketing" label="Marketing">
                Marketing
              </Select.Item>
              <Select.Item value="customize" label="Customize">
                Customize
              </Select.Item>
            </Select.Menu>
          </Select>
        </DepartmentSelectWrapper> */}
      </TabRowContainer>
      <HeaderEmailModal
        isOpen={isEmailModalOpen}
        onClose={handleCloseEmailModal}
        onRequestRemove={handleRequestRemoveEmail}
      />
      <HeaderPhoneModal
        isOpen={isPhoneModalOpen}
        onClose={handleClosePhoneModal}
        onRequestRemove={handleRequestRemovePhone}
      />
      <RemoveConfirmModal
        isOpen={isRemoveEmailModalOpen}
        onClose={handleCloseRemoveEmailModal}
        title="Remove email?"
        description={`You're removing ${pendingRemoveEmail?.email ?? ""} from this contact.`}
        confirmLabel="Remove email"
        onConfirm={handleConfirmRemoveEmail}
      />
      <RemoveConfirmModal
        isOpen={isRemovePhoneModalOpen}
        onClose={handleCloseRemovePhoneModal}
        title="Remove phone number?"
        description="This phone number will be removed from the contact and won't be used for calls or messages anymore. You can add it back later if needed."
        confirmLabel="Remove number"
        onConfirm={handleConfirmRemovePhone}
      />
    </HeaderMasterContainer>
  );
});

export default Header;
